import type { AmmoRecipeFillResult } from '../../app/api/agent'
import type { ReloaderRecipeFormValues } from '../../app/models/reloaderRecipe'

const RECIPE_BULLET_TYPES = ['FMJ', 'JHP', 'SP', 'HPBT', 'other'] as const

function normalizeRecipeBulletType (value: string | null | undefined): string {
  if (!value?.trim()) return ''
  const upper = value.trim().toUpperCase()
  if (upper === 'HPBT' || upper === 'BTHP') return 'HPBT'
  if (upper === 'JHP' || upper === 'HP') return 'JHP'
  if (RECIPE_BULLET_TYPES.includes(upper as (typeof RECIPE_BULLET_TYPES)[number])) {
    return upper === 'OTHER' ? 'other' : upper
  }
  if (upper.includes('FMJ')) return 'FMJ'
  if (upper.includes('SOFT') || upper === 'SP') return 'SP'
  return 'other'
}

function optionalNumber (value: number | null | undefined): number | undefined {
  if (value == null || value <= 0) return undefined
  return value
}

function optionalString (value: string | null | undefined): string {
  return value?.trim() ?? ''
}

export function resolveRecipeCartridgeSpecId (
  result: AmmoRecipeFillResult,
  catalogIds: Set<string>
): string {
  const matched = result.matchedCartridgeSpecId?.trim()
  if (matched && catalogIds.has(matched)) return matched

  const direct = result.cartridgeSpecId?.trim()
  if (direct && catalogIds.has(direct)) return direct

  return ''
}

export function applyAmmoRecipeFill (
  result: AmmoRecipeFillResult,
  catalogIds: Set<string>,
  current: ReloaderRecipeFormValues
): ReloaderRecipeFormValues {
  const cartridgeSpecId = resolveRecipeCartridgeSpecId(result, catalogIds)

  return {
    ...current,
    cartridgeSpecId: cartridgeSpecId || current.cartridgeSpecId,
    powderBrand: optionalString(result.powderBrand) || current.powderBrand,
    powderType: optionalString(result.powderType) || current.powderType,
    chargeWeightGrMin: optionalNumber(result.chargeWeightGrMin) ?? current.chargeWeightGrMin,
    chargeWeightGrMax: optionalNumber(result.chargeWeightGrMax) ?? current.chargeWeightGrMax,
    primerBrand: optionalString(result.primerBrand) || current.primerBrand,
    primerType: optionalString(result.primerType) || current.primerType,
    brassBrand: optionalString(result.brassBrand) || current.brassBrand,
    bulletBrand: optionalString(result.bulletBrand) || current.bulletBrand,
    bulletWeightGr: optionalNumber(result.bulletWeightGr) ?? current.bulletWeightGr,
    bulletType: normalizeRecipeBulletType(result.bulletType) || current.bulletType,
    oalIn: optionalNumber(result.oalIn) ?? current.oalIn,
    coalIn: optionalNumber(result.coalIn) ?? current.coalIn,
    seatingDepthIn: optionalNumber(result.seatingDepthIn) ?? current.seatingDepthIn,
    estimatedVelocityFps: optionalNumber(result.estimatedVelocityFps) ?? current.estimatedVelocityFps,
    chronoData: optionalString(result.chronoData) || current.chronoData,
    intendedUse: optionalString(result.intendedUse) || current.intendedUse || 'target',
    notes: optionalString(result.notes) || current.notes
  }
}

export function isAmmoRecipeFillResultValid (result: unknown): result is AmmoRecipeFillResult {
  if (!result || typeof result !== 'object') return false
  const r = result as AmmoRecipeFillResult

  const hasPowder =
    optionalString(r.powderBrand).length > 0 ||
    optionalString(r.powderType).length > 0 ||
    optionalNumber(r.chargeWeightGrMin) != null ||
    optionalNumber(r.chargeWeightGrMax) != null

  const hasBullet =
    optionalString(r.bulletBrand).length > 0 ||
    optionalNumber(r.bulletWeightGr) != null

  return hasPowder || hasBullet
}
